import { Fragment, useEffect, useMemo, useState } from "react";
import { Transition } from "@headlessui/react";
import moment from "moment-timezone";
import { Button, Input, Table } from "src/components";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/select.component";
import { usePagination } from "src/hooks/usePagination";
import { useOutsideClick } from "src/hooks/useOutsideClick";
import { toast } from "src/hooks/useToast";
import { getStockistProductMapList } from "src/services/invoice-api";
import { getAllCompanyDetails } from "src/services/company-api";
import { fetchStockistFilterData } from "src/services/order-api";
import filterIcon from "src/assets/filterIcon.svg";
import StockistMappingFiltersComponent from "src/features/fields/container/StockistMappingFilters";

const DATE_TIME_FORMAT = "DD-MMM-YY HH:mm:ss";

const searchOptions = [
  { label: "Item Code", value: "item_code" },
  { label: "Item / Description", value: "search" },
];

const getStockistMappingColumns = () => [
  {
    id: "sno",
    header: "Sno",
    cell: (info: any) => info.row.index + 1,
  },
  {
    header: "Stockist ID",
    accessorKey: "stockist_id",
  },
  {
    header: "Stockist Code",
    accessorKey: "stockist_code",
  },
  {
    header: "Item Code",
    accessorKey: "item_code",
  },
  {
    header: "Stockist Item Description",
    accessorKey: "stockist_item_description",
  },
  {
    header: "Item Description",
    accessorKey: "item_description",
  },
  {
    header: "Created By",
    accessorKey: "created_by_name",
    cell: (info: any) => info.getValue() || info.row.original?.created_by || "-",
  },
  {
    header: "Created Date",
    accessorKey: "created_date",
    // API returns UTC timestamps — convert to IST before display.
    cell: (info: any) => (info.getValue() ? moment.utc(info.getValue()).tz("Asia/Kolkata").format(DATE_TIME_FORMAT) : "-"),
  },
];

const StockistMappingDetails = () => {
  const { pagination, onPaginationChange, pageIndex, pageSize, resetPagination } = usePagination();

  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [totalRowCount, setTotalRowCount] = useState(0);

  const [searchCategory, setSearchCategory] = useState(searchOptions[0].value);
  const [searchValue, setSearchValue] = useState("");

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [companyDetails, setCompanyDetails] = useState<any[]>([]);
  const [stockistDetails, setStockistDetails] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<any[]>([]);
  const [selectedStockist, setSelectedStockist] = useState<any[]>([]);
  const filterRef = useOutsideClick(() => setIsFilterOpen(false));

  // Company list loads once; Stockist list is scoped to the selected company — same as the Orders filter.
  useEffect(() => {
    getAllCompanyDetails()
      .then((res: any) => setCompanyDetails(res?.data ?? []))
      .catch((error) => console.error("Error fetching company details:", error));
  }, []);

  useEffect(() => {
    const companyCode = selectedCompany?.[0]?.code;
    if (!companyCode) {
      setStockistDetails([]);
      return;
    }
    fetchStockistFilterData(companyCode)
      .then((res: any) => {
        const rows = res?.data?.data?.data ?? [];
        // Show/search the stockist by its PH code alongside the name.
        const withDisplayLabel = rows.map((row: any) => ({
          ...row,
          stockist_display: [row.medvol_code || row.company_stockist_code || row.stockist_medvol_code, row.owner_name]
            .filter(Boolean)
            .join(" - "),
        }));
        setStockistDetails(withDisplayLabel);
      })
      .catch((error) => console.error("Error fetching stockist details:", error));
  }, [selectedCompany]);

  const fetchStockistMapping = async () => {
    setIsLoading(true);
    try {
      const res: any = await getStockistProductMapList({
        page: pageIndex,
        page_size: pageSize,
        ...(searchValue ? { [searchCategory]: searchValue } : {}),
        ...(selectedCompany?.[0]?.code ? { company_code: selectedCompany[0].code } : {}),
        ...(selectedStockist?.[0]?.stockist_id ? { stockist_id: selectedStockist[0].stockist_id } : {}),
      });
      setData(res?.data?.data?.data ?? []);
      setTotalRowCount(res?.data?.data?.total ?? 0);
    } catch (error) {
      console.error("Error fetching stockist product mapping:", error);
      toast({ description: "Failed to fetch stockist mapping details", variant: "destructive" });
      setData([]);
      setTotalRowCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStockistMapping();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, pageSize, searchValue, searchCategory, selectedCompany, selectedStockist]);

  // Search runs automatically (debounced, like the Invoice Entry search) — no submit button.
  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    resetPagination();
  };

  const handleCategoryChange = (value: string) => {
    setSearchCategory(value);
    resetPagination();
  };

  const handleCompanyChange = (details: any[]) => {
    setSelectedCompany(details);
    setSelectedStockist([]);
    resetPagination();
  };

  const handleStockistChange = (details: any[]) => {
    setSelectedStockist(details);
    resetPagination();
  };

  const handleResetFilter = () => {
    setSelectedCompany([]);
    setSelectedStockist([]);
    setStockistDetails([]);
    resetPagination();
  };

  const activeFilterCount = (selectedCompany.length > 0 ? 1 : 0) + (selectedStockist.length > 0 ? 1 : 0);

  const pageCount = Math.ceil(totalRowCount / (pageSize || 1));

  const columns = useMemo(() => getStockistMappingColumns(), []);

  return (
    <div className="w-full">
      <Transition
        as={Fragment}
        show={isFilterOpen}
        enter="transition ease-in-out duration-200 transform"
        enterFrom="translate-x-full"
        enterTo="translate-x-0"
        leave="transition ease-in-out duration-200 transform"
        leaveFrom="translate-x-0"
        leaveTo="translate-x-full"
      >
        <div className="fixed right-0 inset-0 top-0 z-[500]">
          <StockistMappingFiltersComponent
            ref={filterRef}
            companyDetails={companyDetails}
            stockistDetails={stockistDetails}
            selectedCompany={selectedCompany}
            selectedStockist={selectedStockist}
            onCompanyChange={handleCompanyChange}
            onStockistChange={handleStockistChange}
            onReset={handleResetFilter}
            onClose={() => setIsFilterOpen(false)}
          />
        </div>
      </Transition>

      <div className="flex items-center justify-end gap-3 py-6">
        <div className="flex items-center w-[400px] rounded-md overflow-hidden border border-gray-300 bg-white">
          <Select value={searchCategory} onValueChange={handleCategoryChange}>
            <SelectTrigger className="w-[260px] rounded-none border-none bg-white text-gray-700 focus:ring-0 focus:outline-none focus:shadow-none">
              <SelectValue placeholder="Search By" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {searchOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            placeholder="Search"
            debounceTime={1250}
            value={searchValue}
            onChange={(e) => handleSearchChange(e.target.value.trim())}
            containerClasses="w-full px-3 py-1 text-gray-700 border-none bg-white focus:outline-none focus:ring-0 focus:shadow-none"
          />
        </div>
        <div className="flex items-center gap-2 h-full relative">
          {activeFilterCount > 0 && (
            <span className="absolute top-[-10px] right-[-10px] w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white text-xs">
              {activeFilterCount}
            </span>
          )}
          <Button className="bg-[#F1F1F1]" onClick={() => setIsFilterOpen(true)}>
            <img src={filterIcon} alt="filter-icon" />
          </Button>
        </div>
      </div>

      <Table
        columns={columns}
        data={data}
        isLoading={isLoading}
        showSearch={false}
        headClass="bg-[#F8F8F8]"
        manualPagination={true}
        pageCount={pageCount}
        pagination={pagination}
        onPaginationChange={onPaginationChange}
      />
    </div>
  );
};

export default StockistMappingDetails;
